import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { ConselhoMinutesRow } from '@/services/types';

/**
 * How the minutes NAME each member where they were not relator: counts per
 * minuted position, and the outvoted share with its 90% Wilson interval.
 *
 * This replaced the "named follow share" (audit 2026-09-27): the minutes name
 * members mostly when they are outvoted and record the relator's side on few
 * items, so no follow rate can be read off them. A table of counts, never an
 * interval chart, so nothing here looks like a ranking.
 */
export function MinutesTable({ rows, linked = true }: { rows: ConselhoMinutesRow[]; linked?: boolean }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 });
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="minutes-table">
        <thead className="sticky top-0 z-10 bg-muted text-left">
          <tr>
            <th className="p-2">{t('cade:conselho.minutes.member')}</th>
            <th className="p-2 text-right">{t('cade:conselho.minutes.sided')}</th>
            <th className="p-2 text-right">{t('cade:conselho.minutes.losing')}</th>
            <th className="p-2 text-right">{t('cade:conselho.minutes.agreeing')}</th>
            <th className="p-2 text-right">{t('cade:conselho.minutes.dissenting')}</th>
            <th className="p-2 text-right">{t('cade:conselho.minutes.recused')}</th>
            <th className="p-2 text-right">{t('cade:conselho.minutes.losingShare')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.slug}
              className={`border-t ${r.suppressed ? 'text-muted-foreground' : ''}`}
              data-suppressed={r.suppressed ? 'true' : 'false'}
            >
              <td className="p-2">
                {linked ? (
                  <Link to={`/cade/r/${r.slug}`} className="underline-offset-4 hover:underline">
                    {r.name}
                  </Link>
                ) : (
                  r.name
                )}
              </td>
              <td className="p-2 text-right tabular-nums">{number.format(r.sided)}</td>
              <td className="p-2 text-right tabular-nums">{number.format(r.losing)}</td>
              <td className="p-2 text-right tabular-nums">{number.format(r.agreeing)}</td>
              <td className="p-2 text-right tabular-nums">{number.format(r.dissenting)}</td>
              <td className="p-2 text-right tabular-nums">{number.format(r.recused)}</td>
              <td className="p-2 text-right tabular-nums" data-testid="minutes-share">
                {r.losing_share !== null ? percent.format(r.losing_share) : '—'}
                {r.losing_lower !== null && r.losing_upper !== null ? (
                  <>
                    {' '}
                    <span className="text-muted-foreground">
                      [{percent.format(r.losing_lower)}, {percent.format(r.losing_upper)}]
                    </span>
                  </>
                ) : null}
                {r.suppressed ? ` (${t('cade:conselho.pairs.belowFloor')})` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
